/**
 * @fileoverview Model Selector Component
 * This component provides a dropdown interface for selecting between different
 * AI models (Claude, DeepSeek, MJ Lab). It includes haptic feedback support
 * and accessibility features.
 * 
 * Features:
 * - Model selection via dropdown
 * - Haptic feedback integration
 * - Accessibility support
 * - Responsive design
 * - Error handling for haptic feedback
 * - Performance optimization
 * 
 * @module components/chat/model-selector
 */

import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Brain, Check } from "lucide-react";
import { useHapticFeedback } from "@/hooks/use-haptic";
import { cn } from "@/lib/utils";

/** @typedef {("claude" | "deepseek" | "mjlab")} ModelType */
type ModelType = "claude" | "deepseek" | "mjlab";

type ModelSelectorProps = {
  selectedModel: ModelType;
  onModelChange: (model: ModelType) => void;
  disabled?: boolean;
  showMjLab?: boolean;
  className?: string;
};

export function ModelSelector({ selectedModel, onModelChange, disabled, showMjLab, className }: ModelSelectorProps) {
  const { triggerHaptic } = useHapticFeedback();

  // Use useCallback with a stable reference to prevent unnecessary re-renders
  const handleModelChange = React.useCallback(async (model: ModelType) => {
    // Don't process if already changing or if same model is selected
    if (model === selectedModel) {
      return;
    }
    
    try {
      // Trigger haptic feedback without waiting for animation frame
      await triggerHaptic('selection');
    } catch (error) {
      console.warn('[ModelSelector] Haptic feedback failed:', error);
    }
    
    // Call the change handler immediately to improve perceived performance
    onModelChange(model);
  }, [selectedModel, onModelChange, triggerHaptic]);

  const getModelDisplayName = (model: ModelType, isMobile: boolean = false) => {
    if (isMobile) {
      switch (model) {
        case "claude":
          return "Claude";
        case "deepseek":
          return "DeepSeek";
        case "mjlab":
          return "MJ Lab";
        default:
          return model;
      }
    }

    switch (model) {
      case "claude":
        return "Claude 3.5 Sonnet";
      case "deepseek":
        return "DeepSeek R1";
      case "mjlab":
        return "MJ Lab (Test Mode)";
      default:
        return model;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "model-selector-btn h-10 transform-gpu will-change-transform",
            className
          )}
          disabled={disabled}
          aria-label={`Select AI model - currently using ${getModelDisplayName(selectedModel)}`}
          style={{ contain: "layout" }}
        >
          <Brain className="h-4 w-4" aria-hidden="true" />
          <span className="ml-2 hidden md:inline whitespace-nowrap font-semibold">
            {getModelDisplayName(selectedModel)}
          </span>
          <span className="ml-2 md:hidden whitespace-nowrap font-semibold">
            {getModelDisplayName(selectedModel, true)}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        className="min-w-[120px] transform-gpu will-change-transform"
        style={{
          contain: "layout style paint",
          transition: "transform 150ms ease, opacity 150ms ease"
        }}
      >
        <DropdownMenuItem
          onClick={() => handleModelChange("claude")}
          className="flex items-center justify-between gap-2 px-3 py-2 transform-gpu"
          aria-checked={selectedModel === "claude"}
        >
          <span className="whitespace-nowrap md:hidden font-semibold">Claude</span>
          <span className="whitespace-nowrap hidden md:inline font-semibold">Claude 3.5 Sonnet</span>
          {selectedModel === "claude" && <Check className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleModelChange("deepseek")}
          className="flex items-center justify-between gap-2 px-3 py-2 transform-gpu"
          aria-checked={selectedModel === "deepseek"}
        >
          <span className="whitespace-nowrap md:hidden font-semibold">DeepSeek</span>
          <span className="whitespace-nowrap hidden md:inline font-semibold">DeepSeek R1</span>
          {selectedModel === "deepseek" && <Check className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
        </DropdownMenuItem>
        {showMjLab && (
          <DropdownMenuItem
            onClick={() => handleModelChange("mjlab")}
            className="flex items-center justify-between gap-2 px-3 py-2 transform-gpu"
            aria-checked={selectedModel === "mjlab"}
          >
            <span className="whitespace-nowrap md:hidden font-semibold">MJ Lab</span>
            <span className="whitespace-nowrap hidden md:inline font-semibold">MJ Lab (Test Mode)</span>
            {selectedModel === "mjlab" && <Check className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}